import { getApiErrorMessage } from "./authRedirect";

export const MAX_SCHEDULED_VISITS_PER_PROPERTY_PER_DAY = 2;

export const DAILY_VISIT_LIMIT_MESSAGE =
  "Você já possui 2 visitas agendadas para este imóvel neste dia.";

const CALENDAR_DATE_KEY_PATTERN = /^(\d{4}-\d{2}-\d{2})/;

export type CountableVisitAppointment = {
  id?: number;
  date: string;
  property_id: number;
};

export function getCalendarDateKey(dateTime: string): string | null {
  const match = String(dateTime).trim().match(CALENDAR_DATE_KEY_PATTERN);
  return match ? match[1] : null;
}

export function countScheduledVisitsOnDate(
  appointments: CountableVisitAppointment[],
  propertyId: number,
  dateKey: string,
  excludeScheduleId?: number
): number {
  return appointments.filter((appointment) => {
    if (appointment.property_id !== propertyId) {
      return false;
    }

    if (
      excludeScheduleId != null &&
      appointment.id != null &&
      appointment.id === excludeScheduleId
    ) {
      return false;
    }

    return getCalendarDateKey(appointment.date) === dateKey;
  }).length;
}

export function isDailyVisitLimitReached(
  appointments: CountableVisitAppointment[],
  propertyId: number,
  dateKey: string,
  excludeScheduleId?: number
): boolean {
  return (
    countScheduledVisitsOnDate(
      appointments,
      propertyId,
      dateKey,
      excludeScheduleId
    ) >= MAX_SCHEDULED_VISITS_PER_PROPERTY_PER_DAY
  );
}

export function filterScheduleSlotsByDailyVisitLimit<T extends { date: string }>(
  slots: T[],
  appointments: CountableVisitAppointment[],
  propertyId: number,
  excludeScheduleId?: number
): T[] {
  if (!Number.isFinite(propertyId)) {
    return slots;
  }

  return slots.filter((slot) => {
    const dateKey = getCalendarDateKey(slot.date);
    if (!dateKey) {
      return true;
    }

    return !isDailyVisitLimitReached(
      appointments,
      propertyId,
      dateKey,
      excludeScheduleId
    );
  });
}

export function filterRescheduleSlotsByDailyVisitLimit<T extends { date: string }>(
  slots: T[],
  appointments: CountableVisitAppointment[],
  propertyId: number,
  fromDateTime?: string
): T[] {
  const fromDateKey = fromDateTime ? getCalendarDateKey(fromDateTime) : null;

  if (!Number.isFinite(propertyId)) {
    return slots;
  }

  return slots.filter((slot) => {
    const dateKey = getCalendarDateKey(slot.date);
    if (!dateKey) {
      return true;
    }

    if (fromDateKey && dateKey === fromDateKey) {
      return true;
    }

    return !isDailyVisitLimitReached(appointments, propertyId, dateKey);
  });
}

function getFirstValidationError(error: unknown): string | null {
  if (!error || typeof error !== "object" || !("response" in error)) {
    return null;
  }

  const errors = (
    error as { response?: { data?: { errors?: Record<string, unknown> } } }
  ).response?.data?.errors;

  if (!errors || typeof errors !== "object") {
    return null;
  }

  for (const value of Object.values(errors)) {
    if (typeof value === "string" && value.trim()) {
      return value;
    }

    if (Array.isArray(value) && typeof value[0] === "string" && value[0].trim()) {
      return value[0];
    }
  }

  return null;
}

export function getScheduleApiErrorMessage(
  error: unknown,
  fallback = "Erro ao processar agendamento. Verifique sua conexão e tente novamente."
): string {
  const message = getApiErrorMessage(error, "");
  if (message) {
    return message;
  }

  return getFirstValidationError(error) ?? fallback;
}
